import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useSelector } from "react-redux";

const SchemeDocumentRatioOpen = () => {
  const pid = useSelector((state) => state.user.projectId);
  const [data, setData] = useState({ scheme_count: 0, document_count: 0 });
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State for info popups
  const [infoPopup, setInfoPopup] = useState({ show: false, title: '', description: '', example: '' });

  // Definitions for info popups
  const definitions = {
    "Service Distribution (Schemes vs. Documents)": {
      title: "Service Distribution (Schemes vs. Documents)",
      description: "This dashboard shows the distribution between Scheme-based services and Document-based services for applications in Open status.",
      example: "• Schemes: PM Awas Yojana, Ayushman Bharat, PM Kisan\n• Documents: Aadhaar, PAN Card, Income Certificate, Caste Certificate"
    },
    "State Filter": {
      title: "State Filter",
      description: "Filter the service distribution data by state to analyze regional variations in scheme vs document usage.",
      example: "Select a state to see how services are distributed in that specific region."
    },
    "Total Applications in Open Status": {
      title: "Total Applications in Open Status",
      description: "The total number of applications that are currently open. This represents the combined count of Scheme and Document applications that are in 'Open' status.",
      example: "If 100 scheme applications and 50 document applications are open, total = 150"
    },
    "Scheme": {
      title: "Scheme",
      description: "Government scheme-based applications. These are applications for various government welfare schemes and programs.",
      example: "Examples: Housing schemes, Health insurance, Agricultural subsidies, etc."
    },
    "Document": {
      title: "Document",
      description: "Document-based applications. These are applications for obtaining various government documents and certificates.",
      example: "Examples: Aadhaar, PAN Card, Income Certificate, Caste Certificate, etc."
    }
  };

  // Info Button Component
  const InfoButton = ({ title }) => {
    const definition = definitions[title];
    if (!definition) return null;

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setInfoPopup({
            show: true,
            title: definition.title,
            description: definition.description,
            example: definition.example || ''
          });
        }}
        className="group relative ml-1.5 inline-flex items-center justify-center w-4 h-4 flex-shrink-0 rounded-full text-gray-400 transition-colors duration-300 ease-out hover:text-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-indigo-400"
        aria-label={`Info about ${title}`}
      >
        <span className="absolute inset-0 -m-2 rounded-full bg-white/0 ring-1 ring-transparent transition-all duration-300 ease-out group-hover:bg-white/70 group-hover:backdrop-blur-md group-hover:ring-black/5 group-hover:shadow-[0_4px_14px_rgba(99,102,241,0.18)]" />
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="relative w-3.5 h-3.5 transition-transform duration-300 ease-out group-hover:scale-110"
        >
          <circle cx="12" cy="12" r="9.5" />
          <path d="M12 16v-4.5" />
          <path d="M12 7.8h.01" />
        </svg>
      </button>
    );
  };

  // Info Popup Component
  const InfoPopup = () => {
    if (!infoPopup.show) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]" onClick={closeInfoPopup}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-0 overflow-hidden animate-scaleIn" onClick={(e) => e.stopPropagation()}>
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 18a9 9 0 100-18 9 9 0 000 18z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white">{infoPopup.title}</h3>
            </div>
            <button
              onClick={closeInfoPopup}
              className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="px-6 py-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="mt-0.5">
                <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-700 leading-relaxed">{infoPopup.description}</p>
            </div>

            {infoPopup.example && (
              <div className="mt-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">📊</span>
                  <p className="text-sm font-semibold text-gray-800">Example</p>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-line font-mono bg-white/50 p-3 rounded-lg border border-blue-100">
                  {infoPopup.example}
                </p>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeInfoPopup}
                className="px-5 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Close info popup
  const closeInfoPopup = () => {
    setInfoPopup({ show: false, title: '', description: '', example: '' });
  };

  // Color scheme
  const COLORS = {
    scheme: '#A7E1E9',
    document: '#283E5E',
  };

  // Custom label component
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    outerRadius,
    percent,
    index,
    name
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius * 1.2;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#283E5E"
        textAnchor="middle"
        dominantBaseline="central"
        className="text-xs font-medium"
      >
        {`${name} ${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Fetch states list
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await fetch(
          `https://api-project360-insights.haqdarshak.com/kpi/scheme-diversity/unique-state/?pid=${pid}`
        );
        if (!response.ok) throw new Error('Failed to fetch states');
        const data = await response.json();
        setStates(data.states || []);
      } catch (err) {
        console.error('Error fetching states:', err);
      }
    };

    if (pid) fetchStates();
  }, [pid]);

  // Fetch scheme-document data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        let url = `https://api-project360-insights.haqdarshak.com/kpi/scheme-diversity/scheme-document/open/?pid=${pid}`;
        if (selectedState) {
          url += `&state=${selectedState}`;
        }

        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch data');
        const jsonData = await response.json();
        setData(jsonData);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (pid) fetchData();
  }, [pid, selectedState]);

  const { scheme_count = 0, document_count = 0 } = data;
  const total = scheme_count + document_count;
  const schemePercentage = total > 0 ? ((scheme_count / total) * 100).toFixed(1) : 0;
  const documentPercentage = total > 0 ? ((document_count / total) * 100).toFixed(1) : 0;

  const chartData = [
    { 
      name: 'Scheme', 
      value: scheme_count, 
      percentage: schemePercentage, 
      color: COLORS.scheme,
    },
    { 
      name: 'Document', 
      value: document_count, 
      percentage: documentPercentage, 
      color: COLORS.document,
    },
  ];

  return (
    <div className="bg-white rounded-xl ml-[2vw] max-w-[90vw] shadow-md mt-8 h-full">

      {/* Professional State Filter with Clear Button Inside */}
      <div className="px-6 py-5 bg-gray-50/30 border-b border-gray-200">
        <div className="flex items-center gap-4">
          <div className="flex items-center whitespace-nowrap">
            <span className="text-sm font-medium text-gray-700 mr-2">Filter by State</span>
            <InfoButton title="State Filter" />
          </div>
          <div className="relative flex-1 max-w-xs">
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="w-full px-4 py-2.5 pr-12 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent transition-all duration-200 appearance-none cursor-pointer text-gray-700 text-sm hover:border-gray-400"
            >
              <option value="">All States</option>
              {states.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
            
            {/* Dropdown arrow */}
            <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            {/* Clear button inside select - appears when state is selected */}
            {selectedState && (
              <button
                onClick={() => setSelectedState('')}
                className="absolute inset-y-0 right-8 flex items-center px-1 text-gray-400 hover:text-gray-600 transition-colors z-10"
                aria-label="Clear selection"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="relative">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-600"></div>
            <p className="mt-4 text-sm text-gray-500">Loading data...</p>
          </div>
        </div>
      ) : error ? (
        <div className="text-red-500 text-center py-8">{error}</div>
      ) : (
        <div className="flex flex-col md:flex-row gap-8 p-6">
          {/* Chart */}
          <div className="w-full md:w-2/3 h-96">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 50, right: 30, left: 20, bottom: 50 }}>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius="50%"
                  outerRadius="90%"
                  paddingAngle={2}
                  dataKey="value"
                  label={renderCustomizedLabel}
                  labelLine={{ stroke: '#CBD5E1', strokeWidth: 2 }}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name, props) => [
                    `${value.toLocaleString()} (${props.payload.percentage}%)`,
                    name
                  ]}
                  contentStyle={{
                    backgroundColor: '#fff',
                    borderColor: '#e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    padding: '12px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Stats Cards - White Background */}
          <div className="w-full md:w-1/3 space-y-6">
            {/* Total Card */}
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center">
                <h3 className="text-sm font-medium text-gray-600">
                  Total Applications in Open Status
                </h3>
                <InfoButton title="Total Applications in Open Status" />
              </div>
              <p className="text-2xl font-bold text-purple-800 mt-2">
                {total.toLocaleString()}
              </p>
            </div>

            {/* Scheme and Document Cards */}
            <div className="space-y-3">
              {chartData.map((item, index) => (
                <div key={index} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div
                        className="w-4 h-4 rounded-full mr-3" 
                        style={{ backgroundColor: item.color }}
                      ></div>
                      <span className="text-sm font-medium text-gray-700">
                        {item.name}
                        <InfoButton title={item.name} />
                      </span>
                    </div>
                    <span className="text-sm font-bold text-purple-600 bg-pink-50 px-2.5 py-1 rounded-full">
                      {item.percentage}%
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-purple-800 mt-2 ml-7">
                    {item.value.toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Info Popup */}
      <InfoPopup />

      <style jsx>{`
        @keyframes scaleIn {
          from { 
            opacity: 0; 
            transform: scale(0.95) translateY(-10px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </div>
  );
};

export default SchemeDocumentRatioOpen;